import { Input, VisualInput } from '@/components/inputs';
import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { StepPageActions } from '@/global/components';
import { DATA_SOURCE_ICONS } from '@/lib/Icons/DataSourceIcons/constants';
import { DataSource, DataSourceCodeConfig, DataSourceIDEnum, DataSourceType } from '@/models/dataSource';
import { RootState, useAppDispatch } from '@/reducers';
import { useSaveDataSourceMutation } from '@/services/dataSources';
import { Form, Formik, FormikProps, getIn } from 'formik';
import { FC, SVGProps, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

type ShopifySourceDetailsStepProps = {
  dataSourceType: DataSourceType;
  onNextStep?: VoidFunction;
  onCancel?: VoidFunction;
};

type ShopifySourceDetailsForm = Partial<DataSource<DataSourceCodeConfig>>;

export const ShopifySourceDetailsStep: FC<ShopifySourceDetailsStepProps> = ({ dataSourceType, onNextStep, onCancel }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const dataSourceTypeId = dataSourceType.id as DataSourceIDEnum;
  const RawSourceDetailsIcon = DATA_SOURCE_ICONS[dataSourceTypeId];
  const SourceDetailsIcon = (props: SVGProps<SVGSVGElement>) => <RawSourceDetailsIcon className={props.className ?? 'size-[65px]'} />;
  const dataSource = useSelector((state: RootState) => state.dataSources.edit?.dataSource) as ShopifySourceDetailsForm | undefined;

  const dataSourceInfoFormRef = useRef<FormikProps<ShopifySourceDetailsForm>>(null);

  const workspace = useWorkspace();
  const [saveDataSource, { isLoading: isSaveLoading }] = useSaveDataSourceMutation();

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, t('data_source_edit:validations.too_short'))
      .max(50, t('data_source_edit:validations.too_long'))
      .required(t('data_source_edit:validations.required')),
    configuration: Yup.object({
      url: Yup.string()
        .matches(
          /^(?:https?:\/\/)?[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.myshopify\.com\/?$/i,
          t('data_source_edit:validations.not_valid_url')
        )
        .required(t('data_source_edit:validations.required')),
    }),
  });

  const handleNextStep = async () => {
    await dataSourceInfoFormRef.current?.submitForm();
    try {
      const formValues = dataSourceInfoFormRef.current?.values;
      if (dataSourceInfoFormRef.current?.isValid && formValues?.name && formValues.configuration?.url) {
        const response = await saveDataSource({
          workspaceId: workspace.id,
          dataSource: {
            type: DataSourceIDEnum.Shopify,
            name: formValues.name,
            configuration: { url: `https://${formValues.configuration.url.replace(/^[a-zA-Z]+:\/\//, '')}` },
          },
        }).unwrap();
        dispatch(stashDataSource(response.dataSource));
        onNextStep?.();
      }
    } catch {
      // Stay on Source Details so the user can retry after a failed create.
    }
  };

  return (
    <Formik
      innerRef={dataSourceInfoFormRef}
      initialValues={{ ...dataSource }}
      onSubmit={() => undefined}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
    >
      {({ values, errors, handleChange, setFieldError }) => {
        return (
          <>
            <div className='flex grow gap-8'>
              <div className='flex grow flex-col gap-3'>
                <Form>
                  <VisualInput
                    name='type'
                    id='type'
                    label={t('data_source_edit:code.source_details.source_type')}
                    value={t(`data_sources:catalog.${dataSourceTypeId}.title`)}
                    icon={SourceDetailsIcon}
                  />
                  <Input
                    name='name'
                    id='name'
                    type='text'
                    label={t('data_source_edit:code.source_details.source_name')}
                    value={values.name}
                    placeholder={t('data_source_edit:shopify.source_details.source_name_placeholder')}
                    onChange={(e) => {
                      setFieldError('name', undefined);
                      handleChange('name')(e.target.value);
                    }}
                    error={errors.name}
                  />
                  <Input
                    name='configuration.url'
                    id='configuration.url'
                    type='text'
                    label={t('data_source_edit:shopify.source_details.store_url')}
                    value={values.configuration?.url}
                    placeholder={t('data_source_edit:shopify.source_details.store_url_placeholder')}
                    extraInfo={t('data_source_edit:shopify.source_details.store_url_helper')}
                    onChange={(e) => {
                      setFieldError('configuration.url', undefined);
                      handleChange('configuration.url')(e.target.value);
                    }}
                    error={getIn(errors.configuration, 'url')}
                  />
                </Form>
              </div>
            </div>

            <StepPageActions
              primaryButton={{
                text: 'COMMON.confirm',
                onClick: handleNextStep,
                isLoading: isSaveLoading,
              }}
              secondaryButton={{
                text: 'COMMON.cancel',
                onClick: onCancel,
              }}
            />
          </>
        );
      }}
    </Formik>
  );
};
